// metrics.ts — anonymous, aggregate-only engagement telemetry.
//
// Fire trackEvent(verb, kind, id?) when the user saves / opens / completes / uses
// a piece of content. Events are batched and flushed to the Cloudflare endpoint
// fire-and-forget. There is NO pid, NO user_key, NO session id — the server keeps
// only per-content counters, so nothing here is personal data. It must NEVER block
// or surface an error to the app: every failure is swallowed and the batch dropped.
//
// Event grammar mirrors the server whitelist: "<verb>:<kind>[:<id>]".
import { RESEARCH_ENGAGEMENT_URL } from '@/config/backend';

export type EventVerb = 'save' | 'open' | 'complete' | 'use' | 'view';
export type EventKind =
  | 'article' | 'lesson' | 'tool' | 'meditation' | 'step' | 'calm' | 'reflection'
  // pilot behaviour tracking — still anonymous aggregate counters:
  | 'onboarding' | 'screen' | 'app';

const MAX_QUEUE = 50;
const FLUSH_DELAY_MS = 4000;

let queue: string[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

/** Keep ids short and on-grammar so the server never has to reject them. */
function clean(id?: string): string {
  if (!id) return '';
  const safe = id.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 80);
  return safe ? `:${safe}` : '';
}

function flush(): void {
  if (timer) { clearTimeout(timer); timer = null; }
  if (!queue.length) return;
  const events = queue.slice(0, MAX_QUEUE);
  queue = [];
  try {
    // keepalive lets the POST survive a screen/app transition; we never read the
    // response and never retry — analytics is best-effort by design.
    void fetch(RESEARCH_ENGAGEMENT_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ events }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* swallow — telemetry must never affect the app */
  }
}

/** Record one engagement event. Safe to call from anywhere; never throws. */
export function trackEvent(verb: EventVerb, kind: EventKind, id?: string): void {
  try {
    const key = `${verb}:${kind}${clean(id)}`;
    if (queue.length < MAX_QUEUE && !queue.includes(key)) queue.push(key);
    if (!timer) timer = setTimeout(flush, FLUSH_DELAY_MS);
  } catch {
    /* never throw from a telemetry call */
  }
}

// ── Pilot behaviour helpers (anonymous, aggregate) ─────────────────────────

/** Onboarding funnel step reached, e.g. trackOnboarding('start' | 'consent' | 'finished'). */
export function trackOnboarding(step: string): void {
  trackEvent('view', 'onboarding', step);
}

/** A main screen / feature was opened, e.g. trackScreen('today' | 'library'). */
export function trackScreen(name: string): void {
  trackEvent('view', 'screen', name);
}

/**
 * Coarse, identity-free retention. Once per calendar day per device, emit the
 * tenure bucket of this open (d0 = first day ... d30plus). No id, no session is
 * stored server-side — only "how many opens came from new vs returning users".
 * All state is local (localStorage); any failure is swallowed.
 */
export function trackAppOpen(): void {
  try {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD (UTC)
    let firstOpen: string | null = null;
    try {
      firstOpen = localStorage.getItem('re_first_open');
      if (!firstOpen) { localStorage.setItem('re_first_open', today); firstOpen = today; }
      if (localStorage.getItem('re_last_open_day') === today) return; // once/day
      localStorage.setItem('re_last_open_day', today);
    } catch {
      // No storage (private mode etc.): count it as a plain open, no tenure.
      trackEvent('open', 'app', 'unknown');
      return;
    }
    const days = Math.max(0, Math.floor((Date.parse(today) - Date.parse(firstOpen)) / 86_400_000));
    const bucket = days === 0 ? 'd0' : days === 1 ? 'd1' : days <= 6 ? 'd2_6' : days <= 29 ? 'd7_29' : 'd30plus';
    trackEvent('open', 'app', bucket);
  } catch {
    /* never throw from a telemetry call */
  }
}
